import { useState } from 'react';
import { Button, Dialog, Input, Select, Alert, Card, useToast } from '@dreamtree-org/twreact-ui';
import { Truck } from 'lucide-react';
import { PageHeader, QueryState, EmptyState, StatusBadge } from '@/components';
import { errorText, formatINR, formatDate } from '@/lib/format';
import { useQuery, useMutation } from '@/lib/useQuery';
import StatusTabs from '@/merchant/components/StatusTabs';
import { COURIERS } from '@/merchant/lib/orders';

const TABS = [
  { key: 'to_ship', label: 'To ship' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
  { key: '', label: 'All' },
];
const LINE_LABEL = { to_ship: 'To ship', shipped: 'Shipped', delivered: 'Delivered' };

/**
 * Orders to ship (B17, D76/D79). Each line is one of your products in a paid
 * order: pack it, ship it to the customer with your courier, and enter the
 * tracking number. The store marks the order delivered.
 */
export default function Orders() {
  const { toast } = useToast();
  const [tab, setTab] = useState('to_ship');
  const lines = useQuery('vendorOrders', tab ? { status: tab } : {}, { select: (r) => r.data || [] });
  const shipM = useMutation('vendorShipLine');
  const [shipping, setShipping] = useState(null); // { line, carrier, tracking }
  const [error, setError] = useState('');

  const ship = async () => {
    setError('');
    try {
      await shipM.run({ id: shipping.line.id, carrier: shipping.carrier, tracking: shipping.tracking });
      toast.success('Marked shipped', `${shipping.line.title} · ${shipping.line.order}`);
      setShipping(null);
      lines.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Orders to ship" subtitle="Your products customers bought. Ship each one yourself and add the tracking number so the customer and the store can follow it." />
      <StatusTabs tabs={TABS} value={tab} onChange={setTab} />
      <QueryState query={lines} skeleton="cards">
        {(rows) => (rows && rows.length ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {rows.map((l) => (
              <Card key={l.id} className="p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold">{l.title}</div>
                    <div className="text-xs opacity-70">Order {l.order} · {formatDate(l.placed_at)} · {l.sku} · Qty {l.qty}</div>
                  </div>
                  <StatusBadge status={l.status} label={LINE_LABEL[l.status]} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><div className="text-xs opacity-70">Sale</div>{formatINR(l.line_paise)}</div>
                  <div><div className="text-xs opacity-70">Commission ({l.commission_pct}%)</div>{formatINR(l.commission_paise)}</div>
                  <div><div className="text-xs opacity-70">You earn</div><span className="font-semibold">{formatINR(l.earning_paise)}</span></div>
                </div>
                {l.ship_to ? (
                  <div className="text-sm rounded-md bg-[var(--app-surface-muted,transparent)] border border-[var(--app-border)] p-3">
                    <div className="text-xs opacity-70 mb-1">Ship to</div>
                    <div className="font-medium">{l.ship_to.name} · {l.ship_to.phone}</div>
                    <div>{[l.ship_to.line1, l.ship_to.line2].filter(Boolean).join(', ')}</div>
                    <div>{l.ship_to.city}, {l.ship_to.state} {l.ship_to.pin}</div>
                  </div>
                ) : null}
                {l.tracking ? <div className="text-sm">Shipped with <span className="font-medium">{l.carrier}</span> · <code>{l.tracking}</code>{l.shipped_at ? ` · ${formatDate(l.shipped_at)}` : ''}</div> : null}
                {l.status === 'to_ship' ? (
                  ['paid', 'accepted', 'packed'].includes(l.order_status) ? (
                    <Button leftIcon={<Truck size={16} />} onClick={() => { setError(''); setShipping({ line: l, carrier: COURIERS[0], tracking: '' }); }}>Mark shipped</Button>
                  ) : <p className="text-xs opacity-70">This order is {String(l.order_status).replace(/_/g, ' ')}; do not ship it.</p>
                ) : null}
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState title={tab === 'to_ship' ? 'Nothing to ship' : 'No orders here'}>When a customer buys one of your products, it shows up here with the address to ship to.</EmptyState>
        ))}
      </QueryState>

      <Dialog isOpen={!!shipping} onClose={() => setShipping(null)} title="Mark as shipped" size="sm">
        {shipping ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); ship(); }}>
            <p className="text-sm"><span className="font-medium">{shipping.line.title}</span> × {shipping.line.qty} for order {shipping.line.order}.</p>
            <Select label="Courier" options={COURIERS.map((c) => ({ value: c, label: c }))} value={shipping.carrier} onChange={(v) => setShipping({ ...shipping, carrier: v || '' })} required />
            <Input label="Tracking number" value={shipping.tracking} onChange={(e) => setShipping({ ...shipping, tracking: e.target.value.toUpperCase() })} required helperText="6–30 letters, digits or dashes." />
            {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShipping(null)}>Cancel</Button>
              <Button type="submit" loading={shipM.loading} disabled={!shipping.carrier || shipping.tracking.trim().length < 6}>Mark shipped</Button>
            </div>
          </form>
        ) : null}
      </Dialog>
    </div>
  );
}
